// ==================== ENUMS ====================

export enum PageType {
  hub_root = 'hub_root',
  provider_list = 'provider_list',
  provider_detail = 'provider_detail',
  geo_object = 'geo_object',
  geo_direction = 'geo_direction',
  geo_direction_mode = 'geo_direction_mode',
  route = 'route',
  route_mode = 'route_mode',
  hub_index = 'hub_index',
  ports_list = 'ports_list',
  ports_geo = 'ports_geo',
  port_detail = 'port_detail',
}

export enum SeoPageVariant {
  providers = 'providers',
  rates = 'rates',
}

export enum TradeDirection {
  import = 'import',
  export = 'export',
  domestic = 'domestic',
}

// Transport mode for SEO pages (matches TransportMode enum from Prisma)
export type SeoTransportMode = 'ocean' | 'road' | 'rail';

// ==================== REQUEST INTERFACES ====================

export interface CreateSeoPageData {
  title?: string;
  searchTitle?: string;
  h1Text?: string;
  h2Text?: string;
  metaDescription?: string;
  content?: string;
  pageType: PageType;
  pageVariant?: SeoPageVariant;
  direction?: TradeDirection;
  transportMode?: SeoTransportMode;
  geoObjectId?: string;
  originGeoObjectId?: string;
  destinationGeoObjectId?: string;
  providerId?: string;
  isActive?: boolean;
}

export interface UpdateSeoPageData {
  title?: string;
  searchTitle?: string;
  h1Text?: string;
  h2Text?: string;
  metaDescription?: string;
  content?: string;
  isActive?: boolean;
}

export interface GetSeoPagesParams {
  page?: number;
  limit?: number;
  pageType?: PageType;
  pageVariant?: SeoPageVariant;
  search?: string;
  isActive?: string;
}

// ==================== GEO OBJECT REFERENCE ====================

export interface SeoPageGeoObjectRef {
  id: string;
  name: string;
  slug: string;
  country?: { id: string; name: string; slug: string } | null;
}

// ==================== PROVIDER REFERENCE ====================

export interface SeoPageProviderRef {
  id: string;
  companyName: string;
  slug: string;
}

// ==================== RESPONSE DTOs ====================

export interface SeoPageResponseDto {
  id: string;
  slug: string; // Computed dynamically from geo object slugs
  title: string;
  searchTitle: string | null;
  h1Text: string | null;
  h2Text: string | null;
  metaDescription: string | null;
  content: string | null;
  pageType: PageType;
  pageVariant: SeoPageVariant;
  direction: TradeDirection | null;
  transportMode: SeoTransportMode | null;
  geoObjectId: string | null;
  originGeoObjectId: string | null;
  destinationGeoObjectId: string | null;
  providerId: string | null;
  geoObject?: SeoPageGeoObjectRef | null;
  originGeoObject?: SeoPageGeoObjectRef | null;
  destinationGeoObject?: SeoPageGeoObjectRef | null;
  provider?: SeoPageProviderRef | null;
  isActive: boolean;
  lastUpdated: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface SeoPagesResponseDto {
  data: SeoPageResponseDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ==================== PUBLIC DTOs ====================

/**
 * Lightweight SEO page reference for tag/link display
 */
export interface SeoPageTagDto {
  id: string;
  slug: string;
  label: string;
}

export interface SeoPageContentDto {
  slug: string;
  title: string;
  metaDescription: string | null;
  content: string | null;
  pageType: PageType;
  breadcrumbs: {
    label: string;
    href: string;
  }[];
}

// ==================== SEO CONTENT GENERATION ====================

/**
 * Response from the SEO content generation endpoint
 */
export interface GeneratedSeoContentDto {
  searchTitle: string;
  title: string;
  metaDescription: string;
  h1Text: string;
  h2Text: string;
  content?: string;
}

/**
 * Request parameters for SEO content generation (client-safe interface)
 */
export interface GenerateSeoContentParams {
  pageType: PageType;
  pageVariant?: SeoPageVariant;
  direction?: TradeDirection;
  transportMode?: SeoTransportMode;
  geoObjectId?: string;
  originGeoObjectId?: string;
  destinationGeoObjectId?: string;
  providerId?: string;
}

// ==================== BULK ADD ====================

/**
 * A single item in the bulk-check response, representing one possible SEO page
 */
export interface BulkCheckItem {
  pageType: PageType;
  pageVariant: SeoPageVariant;
  direction: TradeDirection | null;
  transportMode: SeoTransportMode | null;
  computedSlug: string;
  exists: boolean;
  existingPageId: string | null;
  isActive: boolean | null;
  /** For route bulk items: the origin geo object for this specific item */
  originGeoObjectId?: string;
  /** For route bulk items: the destination geo object for this specific item */
  destinationGeoObjectId?: string;
}

/**
 * Response from the bulk-check endpoint
 */
export interface BulkCheckResponseDto {
  geoObjectId: string;
  geoObjectName: string;
  geoObjectSlug: string;
  geoObjectIsActive: boolean;
  geoObjectIsVerified: boolean;
  geoObjectIsSupported: boolean;
  items: BulkCheckItem[];
}

/**
 * A single port item in the ports bulk-check response
 */
export interface PortBulkCheckItem {
  portId: string;
  portName: string;
  portSlug: string;
  computedSlug: string;
  exists: boolean;
  existingPageId: string | null;
  isActive: boolean | null;
  portIsActive: boolean;
  portIsVerified: boolean;
  portIsSupported: boolean;
  portIsContainerPort: boolean;
}

/**
 * Response from the ports bulk-check endpoint
 */
export interface PortBulkCheckResponseDto {
  geoObjectId: string;
  geoObjectName: string;
  geoObjectSlug: string;
  items: PortBulkCheckItem[];
}

// ==================== ROUTE BULK ADD ====================

/**
 * Response from the route bulk-check endpoint.
 * Returns the 4 possible route SEO pages (route + 3x route_mode) for an origin-destination pair.
 */
export interface RouteBulkCheckResponseDto {
  originGeoObjectId: string;
  originGeoObjectName: string;
  originGeoObjectSlug: string;
  destinationGeoObjectId: string;
  destinationGeoObjectName: string;
  destinationGeoObjectSlug: string;
  items: BulkCheckItem[];
}
